import Typography from "@portal/components/atoms/v2/typography";
import useRiskLevelProposal from "@portal/hooks/robot/useRiskLevelProposal";
import { useEffect, useMemo } from "react";
import { MainBox } from "../components/mainBox";
import { RiskLevelBarComponent } from "../components/riskLevelBar/RiskLevelBarComponent";
import { RiskLevelChart } from "./RiskLevelChart";
import { GetRiskLevelName } from "../components/helpers/GetRiskLevelName";
import { IPageProps } from "@portal/types/robot/IPageProps";
import { RobotTextResources } from "@portal/assets/text/RobotTextResources";
import { useRobot } from "@portal/app/context/robotProvider";
import { RobotFooterComponent } from "../components/footer";
import { TextLine } from "@portal/components/atoms/v2/textLine";
import { InfoBoxComponent } from "@portal/components/atoms/infoBox";
import useCustomerInfo from "@portal/hooks/useCustomerInfo";
import PageSpinner from "@portal/components/molecules/v2/pageSpinner";

export const RiskLevelPage = ({ centerId, pageIndex, isDefaultLanguage }: IPageProps) => {
  const {
    riskLevelRequest: request,
    savingProposalRequest,
    setRiskLevelResponse,
    setCustomerNumber,
    setCalculationsRiskLevel,
  } = useRobot();
  const savingsAgreementCustomerNumber = savingProposalRequest.customerNumber;
  const response = useRiskLevelProposal(request);
  const { data: customerInfo, isLoading: customerInfoIsLoading } = useCustomerInfo();
  const hasError = response.error !== null && response.error !== undefined;

  const riskLevelProposal = useMemo(() => (response.data ? response.data.find(x => x.default) : undefined), [response.data]);
  const name = GetRiskLevelName(riskLevelProposal?.riskLevel, isDefaultLanguage);
  const isLoading = response.isLoading;
  const texts = isDefaultLanguage ? RobotTextResources.primary : RobotTextResources.secondary;

  useEffect(() => {
    setRiskLevelResponse(response);
  }, [response, setRiskLevelResponse]);

  useEffect(() => {
    if (!riskLevelProposal) return;
    setCalculationsRiskLevel(riskLevelProposal.riskLevel);
  }, [riskLevelProposal, setCalculationsRiskLevel]);

  useEffect(() => {
    if (customerInfo?.contactInfo.customerNumber && !savingsAgreementCustomerNumber) {
      setCustomerNumber(customerInfo?.contactInfo.customerNumber);
    }
  }, [customerInfo?.contactInfo.customerNumber, setCustomerNumber, savingsAgreementCustomerNumber]);

  return (
    <>
      <MainBox>
        <Typography variant="H6">
          {!isLoading && name ? `${texts.PAGE_12_SUBHEADER} ${name} ${texts.PAGE_12_SUBHEADER_B}` : texts.MESSAGE_LOADING}
        </Typography>
        <TextLine />
        <div className="flex flex-col mt-2">
          <RiskLevelBarComponent proposal={riskLevelProposal} isDefaultLanguage={isDefaultLanguage} />
          {isLoading ? (
            <PageSpinner />
          ) : (
            <div className="flex flex-col p-4 border dark:border-blueBorderDark rounded-md bg-pageBoxBackgroundLight dark:bg-pageBoxBackgroundDark h-100">
              <RiskLevelChart riskLevel={riskLevelProposal} isDefaultLanguage={isDefaultLanguage} />
            </div>
          )}
        </div>
        {hasError && <InfoBoxComponent texts={[texts.ERROR_FETCH]} warning />}
      </MainBox>
      <RobotFooterComponent
        pageIndex={pageIndex}
        centerId={centerId}
        primaryButtonText={texts.BUTTON_NEXT}
        secondaryButtonText={texts.BUTTON_BACK}
        disabled={hasError || customerInfoIsLoading}
      />
    </>
  );
};
